import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { FormError, useFieldError } from '@/shared/forms';
import { Button, TextField } from '@/shared/ui';

import { useLogin } from './api';
import styles from './AuthForm.module.css';
import { type LoginValues, loginSchema } from './schemas';

export function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useTranslation();
  const fieldError = useFieldError();
  const login = useLogin();
  const { register, handleSubmit, formState } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { login: '', password: '' },
  });

  const onSubmit = handleSubmit((values) => {
    login.mutate(values, { onSuccess });
  });

  return (
    <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
      <FormError error={login.error} />
      <TextField
        label={t('auth.login')}
        autoComplete="username"
        autoFocus
        error={fieldError(formState.errors.login?.message)}
        {...register('login')}
      />
      <TextField
        label={t('auth.password')}
        type="password"
        autoComplete="current-password"
        error={fieldError(formState.errors.password?.message)}
        {...register('password')}
      />
      <Button type="submit" variant="primary" disabled={login.isPending}>
        {t('auth.signIn')}
      </Button>
      <p className={styles.switch}>
        {t('auth.noAccount')} <Link to="/register">{t('auth.signUp')}</Link>
      </p>
    </form>
  );
}
